/**
 * The one own-account flow (TMMAPP-0138 items 1 and 7): pick one of the person's accounts or add
 * one, then claim the lines as transfers to it. The Tidy up card (`MissingAccountCard`), the
 * register line (`CounterpartyActions`) and Names to review (`PayeeProposalList`) all open it; the
 * caller says how to claim (`claim`: a 4-digit mask, or an identity key).
 *
 * Once the account exists the flow never shows the form again: it moves to a claiming state that
 * names the account. A refused claim stays there with the error, Try again (the same account) and
 * Close, so a second Save can never create a second account. Closing after a refused claim keeps
 * the account and says so, in words true from every entry: choose "This is my account" again
 * (TMMAPP-0139 item 2). A new account starts managed (item 7).
 */
import { useCallback, useEffect, useEffectEvent, useId, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { accountsApi, type Account, type AccountSubtype } from '@/api/accounts';
import { errorCopy } from '@/api/errorCopy';
import type { ClaimTransferLinesResult } from '@/api/transfers';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { AddAccountForm } from './TransferDestinationPanel';

/** Where the flow opens. */
export type OwnAccountStart =
  /** Use one of the person's accounts, or Add account (no account to pick: the form). */
  | { kind: 'choose' }
  | { kind: 'form' }
  /** The account is known: confirm the claim (needs `confirmTitle`). */
  | { kind: 'confirm'; account: Account }
  /** The account is known: claim at once. */
  | { kind: 'claim'; account: Account };

type Step =
  | { kind: 'loading' }
  | { kind: 'pick'; candidates: Account[] }
  | { kind: 'form'; candidates: Account[] }
  | { kind: 'confirm'; account: Account }
  | { kind: 'claiming'; account: Account; createRules: boolean };

export interface OwnAccountForm {
  initialLast4?: string;
  initialIdentifier?: string;
  initialInstitution?: string;
  initialSubtype?: AccountSubtype;
  showHeading?: boolean;
}

interface OwnAccountDialogProps {
  /** The dialog's title on every step but the confirm. */
  title: string;
  start: OwnAccountStart;
  /** The accounts to pick from on `choose`; read from `GET /me/accounts` when absent. */
  accounts?: Account[] | undefined;
  /** The line's account: the form's currency, and never offered as the other account. */
  source: Account | null;
  /** A 4-digit mask the lines print: an account ending in it is offered first. */
  mask?: string | null | undefined;
  operatingCurrency: string | null;
  form?: OwnAccountForm | undefined;
  /** Present: a picked or added account goes to a confirm step with "also future ones". */
  confirmTitle?: ((account: Account) => string) | undefined;
  claim: (account: Account, createRules: boolean) => Promise<ClaimTransferLinesResult>;
  onCreated?: ((account: Account) => void) | undefined;
  onClaimed: (result: ClaimTransferLinesResult, account: Account) => void | Promise<void>;
  onNotify: (message: string) => void;
  onClose: () => void;
}

const FOUR_DIGITS = /^\d{4}$/;

/** The person's other open accounts they own; an account ending in the mask comes first. */
function pickable(accounts: Account[], source: Account | null, mask: string | null): Account[] {
  const open = accounts.filter(
    (account) =>
      account.access === 'OWNER' && account.status !== 'CLOSED' && account.id !== source?.id,
  );
  if (mask === null || !FOUR_DIGITS.test(mask)) return open;
  return [
    ...open.filter((account) => account.last4 === mask),
    ...open.filter((account) => account.last4 !== mask),
  ];
}

function initialStep(
  start: OwnAccountStart,
  accounts: Account[] | undefined,
  source: Account | null,
  mask: string | null,
): Step {
  switch (start.kind) {
    case 'choose': {
      if (accounts === undefined) return { kind: 'loading' };
      const candidates = pickable(accounts, source, mask);
      return candidates.length > 0 ? { kind: 'pick', candidates } : { kind: 'form', candidates };
    }
    case 'form':
      return { kind: 'form', candidates: [] };
    case 'confirm':
      return { kind: 'confirm', account: start.account };
    case 'claim':
      return { kind: 'claiming', account: start.account, createRules: true };
  }
}

export function OwnAccountDialog({
  title,
  start,
  accounts,
  source,
  mask = null,
  operatingCurrency,
  form = {},
  confirmTitle,
  claim,
  onCreated,
  onClaimed,
  onNotify,
  onClose,
}: OwnAccountDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const fullScreen = useDialogFullScreen();
  const [step, setStep] = useState<Step>(() => initialStep(start, accounts, source, mask));
  const [formDirty, setFormDirty] = useState(false);
  const [createRules, setCreateRules] = useState(true);
  const [busy, setBusy] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  // The account this flow added: from then on it only claims (item 1).
  const [created, setCreated] = useState<Account | null>(null);
  const started = useRef(false);
  const handleDirty = useCallback((dirty: boolean) => setFormDirty(dirty), []);

  function close() {
    if (created !== null && claimError !== null) {
      onNotify(t('ownAccount.keptForLater', { account: created.name }));
    }
    onClose();
  }

  const guard = useDialogDirtyGuard(step.kind === 'form' && formDirty, close, busy);

  async function claimNow(account: Account, rules: boolean) {
    if (busy) return;
    setStep({ kind: 'claiming', account, createRules: rules });
    setClaimError(null);
    setBusy(true);
    let result: ClaimTransferLinesResult;
    try {
      result = await claim(account, rules);
    } catch (error) {
      setClaimError(errorCopy(t, error, 'accounts.errorGeneric'));
      setBusy(false);
      return;
    }
    setBusy(false);
    // The claim succeeded: what the caller does next (a toast, a refetch) is never a claim error.
    await onClaimed(result, account);
  }

  function chosen(account: Account) {
    if (confirmTitle !== undefined) setStep({ kind: 'confirm', account });
    else void claimNow(account, true);
  }

  // Once, on open: read the accounts to pick from, or claim an account already known.
  const opened = useEffectEvent(() => {
    if (step.kind === 'claiming') {
      void claimNow(step.account, step.createRules);
      return;
    }
    if (step.kind !== 'loading') return;
    void accountsApi
      .list()
      .then((rows) => pickable(rows, source, mask))
      .catch(() => [])
      .then((candidates) =>
        setStep(
          candidates.length > 0 ? { kind: 'pick', candidates } : { kind: 'form', candidates },
        ),
      );
  });
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    opened();
  }, []);

  function body() {
    switch (step.kind) {
      case 'loading':
        return (
          <>
            <DialogTitle id={titleId}>{title}</DialogTitle>
            <DialogContent>
              <Skeleton variant="rounded" height={96} aria-busy="true" />
            </DialogContent>
          </>
        );
      case 'pick':
        return (
          <>
            <DialogTitle id={titleId}>{title}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t('ownAccount.pickBody')}
              </Typography>
              <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
                {step.candidates.map((account) => (
                  <Button
                    key={account.id}
                    variant="outlined"
                    data-own-account-pick={account.id}
                    onClick={() => chosen(account)}
                  >
                    {t('review.missingAccount.useExisting', { account: account.name })}
                  </Button>
                ))}
                <Button
                  onClick={() => setStep({ kind: 'form', candidates: step.candidates })}
                  data-own-account-add=""
                >
                  {t('transfer.addAccount')}
                </Button>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={close}>{t('common.cancel')}</Button>
            </DialogActions>
          </>
        );
      case 'form': {
        const candidates = step.candidates;
        const back = candidates.length > 0;
        return (
          <>
            <DialogTitle id={titleId}>{title}</DialogTitle>
            <DialogContent>
              <AddAccountForm
                source={source}
                operatingCurrency={operatingCurrency}
                {...form}
                backLabel={back ? t('transfer.back') : t('common.cancel')}
                onBack={back ? () => setStep({ kind: 'pick', candidates }) : guard.requestClose}
                onCreated={(account) => {
                  setCreated(account);
                  onCreated?.(account);
                  chosen(account);
                }}
                onDirtyChange={handleDirty}
                onSubscriptionRequired={onClose}
              />
            </DialogContent>
          </>
        );
      }
      case 'confirm':
        return (
          <>
            <DialogTitle id={titleId}>{confirmTitle?.(step.account) ?? title}</DialogTitle>
            <DialogContent>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createRules}
                    onChange={(_event, checked) => setCreateRules(checked)}
                  />
                }
                label={t('review.missingAccount.alsoFuture')}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={close}>{t('review.missingAccount.notNow')}</Button>
              <SubmitButton onClick={() => void claimNow(step.account, createRules)}>
                {t('review.missingAccount.confirm')}
              </SubmitButton>
            </DialogActions>
          </>
        );
      case 'claiming':
        return (
          <>
            <DialogTitle id={titleId}>{title}</DialogTitle>
            <DialogContent>
              {claimError !== null && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {claimError}
                </Alert>
              )}
              <Typography variant="body2" data-own-account-claiming="">
                {claimError === null
                  ? t('ownAccount.claiming', { account: step.account.name })
                  : // "is saved" only for the account this flow added; one picked was always there.
                    t(
                      created !== null && created.id === step.account.id
                        ? 'ownAccount.claimFailed'
                        : 'ownAccount.claimFailedExisting',
                      { account: step.account.name },
                    )}
              </Typography>
            </DialogContent>
            {claimError !== null && (
              <DialogActions>
                <Button onClick={close}>{t('common.close')}</Button>
                <SubmitButton
                  loading={busy}
                  onClick={() => void claimNow(step.account, step.createRules)}
                >
                  {t('ownAccount.tryAgain')}
                </SubmitButton>
              </DialogActions>
            )}
          </>
        );
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={titleId}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      data-own-account-dialog={step.kind}
    >
      {body()}
    </GuardedDialog>
  );
}
